import { type UploadItem, useUploadStore } from "@/lib/upload/upload-store";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Upload,
  XCircle,
} from "lucide-react";
import { useState } from "react";

export function UploadProgressToast() {
  const items = useUploadStore((s) => s.items);
  const [collapsed, setCollapsed] = useState(false);

  if (items.length === 0) return null;

  const activeCount = items.filter((i) => i.status === "uploading").length;

  return (
    <div className="fixed right-4 bottom-4 z-50 w-72 overflow-hidden rounded-xl border border-border bg-background shadow-2xl sm:right-6 sm:bottom-6">
      {/* Header */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center justify-between gap-2 border-border border-b bg-muted/50 px-4 py-3 text-left hover:bg-muted/80"
      >
        <div className="flex items-center gap-2">
          {activeCount > 0 ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          ) : (
            <Upload className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="font-medium text-sm">
            {activeCount > 0
              ? `${activeCount} video yükleniyor`
              : "Yüklemeler tamamlandı"}
          </span>
        </div>
        {collapsed ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {/* Items */}
      {!collapsed && (
        <div className="max-h-64 divide-y divide-border overflow-y-auto">
          {items.map((item) => (
            <UploadItemRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function UploadItemRow({ item }: { item: UploadItem }) {
  const removeItem = useUploadStore((s) => s._removeItem);

  return (
    <div className="flex flex-col gap-1.5 px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <span
          className="flex-1 truncate text-foreground text-xs"
          title={item.fileName}
        >
          {item.fileName}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {item.status === "uploading" && (
            <span className="text-[11px] text-muted-foreground tabular-nums">
              %{item.progress}
            </span>
          )}
          {item.status === "done" && (
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          )}
          {item.status === "error" && (
            <button
              type="button"
              onClick={() => removeItem(item.id)}
              title="Kapat"
            >
              <XCircle className="h-4 w-4 text-destructive" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {(item.status === "uploading" || item.status === "done") && (
        <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-300",
              item.status === "done" ? "bg-green-500" : "bg-primary",
            )}
            style={{ width: `${item.progress}%` }}
          />
        </div>
      )}

      {/* Error message */}
      {item.status === "error" && (
        <p className="text-[11px] text-destructive">
          Hata: {item.errorMessage ?? "Bilinmeyen hata"}
        </p>
      )}
    </div>
  );
}
